import {
  BaseMeasureController,
  IMeasureSettings,
  TInitializationState,
} from 'core-components/sitebuilder/analytics'
import { IUser } from 'core-components/sitebuilder/models/IUser'
import { IWidget } from 'core-components/sitebuilder/models/IWidget'
import Logger from 'src/core/utils/Logger'

import {
  BASE_TIMER_DATA,
  MAX_MEASURE_TIME,
  MIN_MEASURE_TIME,
} from './constants/MeasureControllerConstants'
import GoogleAnalyticsService from './GoogleAnalyticsService'
import { logMessage } from './helpers/logMessage'
import { sendAnalytics } from './sendAnalytics'
import { TMeasureContainer } from './types/MeasureControllerTypes'

export interface ICoreMeasureSettings extends IMeasureSettings {
  sendLongMeasure?: boolean
}

export default class MeasureController extends BaseMeasureController {
  private readonly widgetId: string
  private user: IUser
  private renderCount: TMeasureContainer = { ...BASE_TIMER_DATA }
  private allTime: TMeasureContainer = { ...BASE_TIMER_DATA }
  private startMountTime: TMeasureContainer = { ...BASE_TIMER_DATA }
  private activeKeys: Set<string> = new Set()
  private timer: TMeasureContainer = { ...BASE_TIMER_DATA }
  private objectType: 'widget' | 'component' = 'widget'
  private readonly sendLongMeasure: boolean

  constructor(
    widgetInfo: IWidget | string,
    { startInitializationMeasure = true, user, sendLongMeasure = false }: ICoreMeasureSettings
  ) {
    super(widgetInfo, { startInitializationMeasure, user })

    const isNameSet = typeof widgetInfo === 'string'
    this.widgetId = isNameSet ? (widgetInfo as string) : (widgetInfo as IWidget).i
    this.user = user
    this.sendLongMeasure = sendLongMeasure

    if (isNameSet) {
      this.objectType = 'component'
    }
    if (startInitializationMeasure) {
      logMessage(`${this.widgetId} initialization`)
    }
  }

  public start = (state: TInitializationState, key: string = 'default') => {
    if (!this.activeKeys.has(key)) {
      this.activeKeys.add(key)
    }

    if (!this.startMountTime[state]) {
      this.startMountTime[state] = new Date().getTime()
      this.longMeasurementAlert(window.location.href, state)
    }
  }

  public stop = (state: TInitializationState, key: string = 'default') => {
    this.activeKeys.delete(key)

    if (this.activeKeys.size > 0) {
      return
    }

    if (!this.startMountTime[state]) {
      this.clear(state)
      return
    }

    this.allTime[state] = Date.now() - this.startMountTime[state]
    clearTimeout(this.timer[state])

    if (this.allTime[state] >= MIN_MEASURE_TIME) {
      this.sendMeasure(state)
      this.renderCount[state]++
    }

    this.clear(state)
  }

  public clearFullMetric = () => {
    for (const [key] of Object.entries(this.startMountTime)) {
      this.startMountTime[key] = 0
    }
    for (const [key] of Object.entries(this.allTime)) {
      this.allTime[key] = 0
    }
    for (const [key] of Object.entries(this.allTime)) {
      clearTimeout(this.timer[key])
    }
  }

  public getWidgetId() {
    return this.widgetId
  }

  private clear(state: TInitializationState) {
    this.startMountTime[state] = 0
    this.allTime[state] = 0
    this.renderCount[state] = 0
  }

  private longMeasurementAlert(point: string, state: TInitializationState) {
    this.timer[state] = window.setTimeout(() => {
      this.timer[state] = 0
      if (this.startMountTime[state] && point === window.location.href) {
        Logger.error(
          `[Measure Controller] :: Widget ${this.widgetId} in state ${state} took more than ${MAX_MEASURE_TIME / 2 / 1000} seconds to load`
        )
      }
    }, MAX_MEASURE_TIME / 2)
  }

  private sendMeasure(state: TInitializationState) {
    const name = `${this.widgetId}`
    const isLongMeasure = this.allTime[state] >= MAX_MEASURE_TIME

    if (isLongMeasure && !this.sendLongMeasure) {
      this.clear(state)
      return
    }

    logMessage(`${name} ${state}: ${this.allTime[state]}ms`)

    if (state === 'renderData') {
      GoogleAnalyticsService.timing(name, 'duration', this.allTime[state])

      sendAnalytics(
        {
          objectId: name,
          objectType: this.objectType,
          objectParams: {
            name: name,
            typeState: state,
            duration: this.allTime[state],
            created: this.startMountTime[state],
            renderCount: this.renderCount[state],
            ...(isLongMeasure && { isLongMeasure }),
          },
        },
        this.user.email
      )
        .then(() => {
          this.clear(state)
        })
        .catch(Logger.error)
    } else {
      this.clear(state)
    }
  }
}
